import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { EnergyLogo, EnergyIcon, EnergyLogoStacked } from '../components/EnergyLogo';
import WhatsAppButton from '../components/WhatsAppButton';
import AgencyMap from '../components/AgencyMap';
import SEO from '../components/SEO';
import { 
  Zap, 
  Banknote, 
  Shield, 
  ArrowRight, 
  CheckCircle2, 
  Users, 
  Building2,
  Phone,
  Mail,
  MapPin,
  ChevronDown,
  Info,
  Home,
  Sparkles
} from 'lucide-react';

const Landing = () => {
  const [servicesMenuOpen, setServicesMenuOpen] = useState(false);
  const [inArrivoOpen, setInArrivoOpen] = useState(false);
  const [inArrivoService, setInArrivoService] = useState(null);

  // Service menu items with routes
  const serviceMenuItems = [
    { id: 'luce_gas', title: 'Luce e Gas', route: '/servizi/luce-gas', icon: Zap, color: 'text-yellow-400' },
    { id: 'finanziamenti', title: 'Finanziamenti', route: '/servizi/finanziamenti', icon: Banknote, color: 'text-green-400' },
    { id: 'assicurazioni', title: 'Assicurazioni', route: '/servizi/assicurazioni', icon: Shield, color: 'text-indigo-400' },
  ];

  const services = [
    {
      id: 'luce_gas',
      title: 'Luce e Gas',
      description: 'Confronta le migliori offerte per energia elettrica e gas naturale. Risparmia sulla bolletta.',
      icon: Zap,
      color: 'from-yellow-500 to-orange-500',
      features: ['Confronto tariffe', 'Voltura gratuita', 'Assistenza dedicata'],
    },
    {
      id: 'finanziamenti',
      title: 'Finanziamenti',
      description: 'Mutui, prestiti personali e cessioni del quinto alle migliori condizioni.',
      icon: Banknote,
      color: 'from-green-500 to-emerald-500',
      features: ['Mutui Casa', 'Prestiti Personali', 'Cessione del Quinto'],
      inArrivo: true,
    },
    {
      id: 'assicurazioni',
      title: 'Assicurazioni',
      description: 'Polizze auto, casa, vita e molto altro per proteggere te e i tuoi cari.',
      icon: Shield,
      color: 'from-indigo-500 to-violet-600',
      features: ['RC Auto e Moto', 'Casa e Famiglia', 'Vita e Infortuni'],
      inArrivo: true,
    },
    {
      id: 'immobiliare',
      title: 'Immobiliare',
      description: 'Compravendita, valutazioni e Property Finder per chi cerca casa con consulenza dedicata e mutuo integrato.',
      icon: Home,
      color: 'from-rose-500 to-purple-700',
      features: ['Vendita Case', 'Valutazione Gratuita', 'Property Finder Attivo'],
      inArrivo: true,
    },
  ];

  return (
    <div className="min-h-screen bg-[#020617]">
      <SEO
        title="Energy Multiservizi | Luce, Gas, CAF, Finanziamenti e Assicurazioni"
        description="Energy Multiservizi: agenzia di fiducia per contratti luce e gas, servizi CAF (730, ISEE), finanziamenti, mutui, prestiti e assicurazioni. Sedi in tutta Italia."
        path="/"
        keywords="energy multiservizi, luce gas, CAF, 730, ISEE, finanziamenti, mutui, assicurazioni, agenzia multiservizi"
      />
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <EnergyLogo variant="header" size="md" />
            <div className="hidden md:flex items-center gap-8">
              {/* Services Dropdown */}
              <div 
                className="relative"
                onMouseEnter={() => setServicesMenuOpen(true)}
                onMouseLeave={() => setServicesMenuOpen(false)}
              >
                <button className="flex items-center gap-1 text-slate-300 hover:text-white transition-colors">
                  Servizi
                  <ChevronDown className={`w-4 h-4 transition-transform ${servicesMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                {servicesMenuOpen && (
                  <div className="absolute top-full left-0 mt-2 w-56 bg-slate-900/95 backdrop-blur-md border border-slate-700 rounded-xl shadow-xl overflow-hidden">
                    {serviceMenuItems.map((item) => (
                      <Link
                        key={item.id}
                        to={item.route}
                        className="flex items-center gap-3 px-4 py-3 text-slate-300 hover:text-white hover:bg-slate-800 transition-colors"
                      >
                        <item.icon className={`w-5 h-5 ${item.color}`} />
                        <span>{item.title}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              <a href="#chi-siamo" className="text-slate-300 hover:text-white transition-colors">Chi Siamo</a>
              <a href="#sedi" className="text-slate-300 hover:text-white transition-colors">Dove Siamo</a>
              <a href="#contatti" className="text-slate-300 hover:text-white transition-colors">Contatti</a>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login?tipo=cliente">
                <Button className="btn-gradient rounded-full" data-testid="nav-login-btn">
                  ACCEDI
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* WhatsApp Floating Button */}
      <WhatsAppButton />

      {/* Hero Section */}
      <section className="hero-gradient pt-32 pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-7 animate-fade-in">
              <span className="inline-block px-4 py-1 rounded-full bg-cyan-500/10 text-cyan-400 text-sm font-medium mb-6">
                La tua agenzia di fiducia
              </span>
              <h1 className="text-4xl md:text-6xl font-heading font-bold text-white leading-tight mb-6">
                Tutti i servizi di cui hai bisogno,{' '}
                <span className="gradient-text">in un unico posto</span>
              </h1>
              <p className="text-lg text-slate-400 mb-8 max-w-xl">
                Energia, servizi fiscali, finanziamenti e assicurazioni. 
                Affidati agli esperti per risparmiare tempo e denaro.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/prenota">
                  <Button size="lg" className="btn-gradient rounded-full px-8" data-testid="hero-cta-btn">
                    Richiedi Appuntamento
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a href="#servizi">
                  <Button size="lg" variant="outline" className="rounded-full px-8 border-slate-700 text-slate-300 hover:bg-slate-800">
                    Scopri i Servizi
                  </Button>
                </a>
              </div>
            </div>
            <div className="md:col-span-5 hidden md:flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 blur-3xl"></div>
                <EnergyLogoStacked size="xl" className="relative drop-shadow-2xl" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Live Stats Section - Auto-updated */}
      <section className="py-16 px-4 border-y border-slate-800 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-blue-500/5"></div>
        <div className="max-w-7xl mx-auto relative">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-heading font-bold text-white">I Nostri Punti di Forza</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-testid="strengths-band">
            <div className="text-center" data-testid="strength-consulenza">
              <div className="mx-auto mb-3 w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-green-400" />
              </div>
              <div className="text-white font-heading font-semibold">Consulenza Gratuita</div>
            </div>
            <div className="text-center" data-testid="strength-voltura">
              <div className="mx-auto mb-3 w-12 h-12 rounded-xl bg-yellow-500/10 flex items-center justify-center">
                <Zap className="w-6 h-6 text-yellow-400" />
              </div>
              <div className="text-white font-heading font-semibold">Voltura Inclusa</div>
            </div>
            <div className="text-center" data-testid="strength-assistenza">
              <div className="mx-auto mb-3 w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-cyan-400" />
              </div>
              <div className="text-white font-heading font-semibold">Assistenza Dedicata</div>
            </div>
            <div className="text-center" data-testid="strength-sedi">
              <div className="mx-auto mb-3 w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <MapPin className="w-6 h-6 text-blue-400" />
              </div>
              <div className="text-white font-heading font-semibold">Sedi Operative Fronte Strada</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="servizi" className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 rounded-full bg-cyan-500/10 text-cyan-400 text-sm font-medium mb-4">
              I Nostri Servizi
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              Soluzioni complete per ogni esigenza
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Scegli il servizio di cui hai bisogno e carica i documenti necessari. 
              Il nostro team si occuperà di tutto il resto.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {services.map((service, index) => {
              // Map service id to route
              const serviceRoute = {
                'luce_gas': '/servizi/luce-gas',
                'finanziamenti': '/servizi/finanziamenti',
                'assicurazioni': '/servizi/assicurazioni'
              }[service.id];
              const isComingSoon = !!service.comingSoon;
              const isInArrivo = !!service.inArrivo;
              const openInArrivo = () => {
                setInArrivoService(service);
                setInArrivoOpen(true);
              };

              return (
                <Card 
                  key={service.id}
                  className={`service-card bg-slate-900/60 border-slate-800 overflow-hidden animate-fade-in stagger-${index + 1} ${isComingSoon || isInArrivo ? 'relative cursor-pointer' : ''}`}
                  data-testid={`service-card-${service.id}`}
                  onClick={isInArrivo ? openInArrivo : undefined}
                >
                  {/* Badge "CS" Coming Soon — in alto a destra */}
                  {isComingSoon && (
                    <div className="absolute top-3 right-3 z-10 flex items-center gap-1">
                      <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-gradient-to-br from-orange-500 to-amber-600 text-white text-[10px] font-bold tracking-wider shadow-lg ring-2 ring-orange-300/30" data-testid="badge-cs-public">
                        CS
                      </span>
                    </div>
                  )}
                  {isInArrivo && (
                    <div className="absolute top-3 right-3 z-10">
                      <span
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gradient-to-r from-amber-500/80 to-orange-500/80 text-white text-[10px] font-bold tracking-wider shadow-lg ring-1 ring-amber-300/40"
                        data-testid={`badge-in-arrivo-${service.id}`}
                      >
                        <Sparkles className="w-3 h-3" />
                        IN ARRIVO
                      </span>
                    </div>
                  )}
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-xl bg-gradient-to-br ${service.color}`}>
                        <service.icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-heading font-semibold text-white mb-2 flex items-center gap-2">
                          {service.title}
                          {isComingSoon && (
                            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-fuchsia-500/15 text-fuchsia-300 border border-fuchsia-400/30 font-semibold">
                              Closed Beta
                            </span>
                          )}
                        </h3>
                        <p className="text-slate-400 mb-4">{service.description}</p>
                        <ul className="space-y-2">
                          {service.features.map((feature, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                              <CheckCircle2 className="w-4 h-4 text-cyan-400" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="mt-6">
                      {isInArrivo ? (
                        <button
                          type="button"
                          onClick={openInArrivo}
                          className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-md bg-gradient-to-r from-amber-500/15 to-orange-500/15 border border-amber-500/40 text-amber-300 font-semibold tracking-wider hover:from-amber-500/25 hover:to-orange-500/25 transition-colors"
                          data-testid={`btn-in-arrivo-${service.id}`}
                        >
                          <Sparkles className="w-4 h-4" />
                          IN ARRIVO
                        </button>
                      ) : isComingSoon ? (
                        // Coming Soon: vetrina chiusa MA Property Finder è già attivo
                        service.ctaPath ? (
                          <div className="flex flex-col gap-2">
                            <Link to={service.ctaPath} className="block">
                              <Button
                                className="w-full bg-gradient-to-r from-rose-500 to-purple-700 hover:from-rose-400 hover:to-purple-600 text-white font-semibold shadow-lg shadow-rose-500/25"
                                data-testid={`btn-cta-${service.id}`}
                              >
                                <Sparkles className="w-4 h-4 mr-2" />
                                {service.ctaLabel || 'Scopri di più'}
                              </Button>
                            </Link>
                            <p className="text-[11px] text-slate-500 text-center">
                              Vetrina B2B in <span className="text-rose-400">closed beta</span> · accesso riservato alle agenzie partner
                            </p>
                          </div>
                        ) : (
                          <button
                            type="button"
                            disabled
                            aria-disabled="true"
                            className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-md bg-slate-800/40 border border-fuchsia-500/30 text-fuchsia-300/90 font-semibold tracking-wider cursor-not-allowed select-none"
                            data-testid="btn-coming-soon-public"
                          >
                            <Sparkles className="w-4 h-4" />
                            COMING SOON
                          </button>
                        )
                      ) : (
                        <div className="flex gap-3">
                          <Link to="/login" className="flex-1">
                            <Button variant="outline" className="w-full border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white">
                              Richiedi Preventivo
                              <ArrowRight className="ml-2 w-4 h-4" />
                            </Button>
                          </Link>
                          <Link to={serviceRoute}>
                            <Button variant="ghost" className="border border-slate-700 text-cyan-400 hover:bg-cyan-500/10 hover:text-cyan-300">
                              <Info className="w-4 h-4 mr-1" />
                              Info
                            </Button>
                          </Link>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="chi-siamo" className="py-20 px-4 bg-slate-900/30">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-1 rounded-full bg-cyan-500/10 text-cyan-400 text-sm font-medium mb-4">
                Chi Siamo
              </span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-6">
                Esperti al tuo servizio da oltre 15 anni
              </h2>
              <p className="text-slate-400 mb-6">
                ENERGY MULTISERVIZI è un'agenzia di intermediazione che offre 
                consulenza professionale in diversi settori: energia, servizi fiscali, 
                finanziamenti e assicurazioni.
              </p>
              <p className="text-slate-400 mb-8">
                Lavoriamo con una rete di sedi associate in crescita, per garantire a ogni cliente un servizio diretto e personalizzato.
              </p>
              <div className="flex gap-8">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-cyan-500/10">
                    <Users className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <div className="text-white font-semibold">Team Dedicato</div>
                    <div className="text-sm text-slate-400">Consulenti esperti</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-cyan-500/10">
                    <Building2 className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <div className="text-white font-semibold">Sedi Selezionate</div>
                    <div className="text-sm text-slate-400">Copertura in crescita</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative flex justify-center">
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 blur-3xl"></div>
              <EnergyLogoStacked size="lg" className="relative" />
            </div>
          </div>
        </div>
      </section>

      {/* Agency Map Section */}
      <section id="sedi" className="py-20 px-4 bg-slate-900/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-6">
              <MapPin className="w-4 h-4 text-cyan-400" />
              <span className="text-cyan-400 text-sm font-medium">Le Nostre Sedi</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              Dove Siamo in Italia
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              La nostra rete di agenzie copre tutto il territorio italiano. 
              Trova la sede più vicina a te e vieni a trovarci.
            </p>
          </div>
          
          <AgencyMap />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="glass rounded-3xl p-12">
            <EnergyIcon size={64} className="mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              Pronto a iniziare?
            </h2>
            <p className="text-slate-400 mb-8 max-w-xl mx-auto">
              Registrati gratuitamente e inizia a gestire le tue pratiche in modo semplice e veloce.
            </p>
            <Link to="/login">
              <Button size="lg" className="btn-gradient rounded-full px-10" data-testid="cta-register-btn">
                Registrati Gratis
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contatti" className="py-16 px-4 border-t border-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="mb-4">
                <EnergyLogo variant="full" size="md" />
              </div>
              <p className="text-slate-400 text-sm">
                La tua agenzia multiservizi di fiducia per energia, fiscale, finanza e assicurazioni.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Servizi</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="#" className="hover:text-white transition-colors">Luce e Gas</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Servizi CAF</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Finanziamenti</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Assicurazioni</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Azienda</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="#chi-siamo" className="hover:text-white transition-colors">Chi Siamo</a></li>
                <li><Link to="/diventa-partner" className="hover:text-white transition-colors">Diventa Partner</Link></li>
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Cookie Policy</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Contatti Sede Principale</h4>
              <ul className="space-y-3 text-sm text-slate-400">
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-cyan-400" />
                  <a href="tel:07751470039" className="hover:text-white transition-colors">0775 1470039</a>
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  <a href="https://wa.me/393932913773" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">393 2913773</a>
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-cyan-400" />
                  <a href="mailto:info@energy-multiservizi.com" className="hover:text-white transition-colors">info@energy-multiservizi.com</a>
                </li>
              </ul>
              <p className="text-xs text-slate-500 mt-3">
                Per contattare il tuo ufficio di riferimento, <Link to="/login?tipo=cliente" className="text-cyan-400 hover:underline">registrati o accedi</Link>.
              </p>
            </div>
          </div>
          
          {/* Social Media Section */}
          <div className="pt-8 border-t border-slate-800">
            <div className="flex flex-col items-center text-center mb-6">
              <p className="text-lg text-white font-semibold mb-2">
                Seguici per sbloccare il tuo risparmio! ✅🚀
              </p>
              <p className="text-sm text-slate-400 max-w-md">
                Strategie e vantaggi esclusivi per ogni tua pratica!
              </p>
            </div>
            <div className="flex items-center justify-center gap-4">
              {/* Facebook */}
              <a
                href="https://www.facebook.com/energytelservice/?locale=it_IT"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#1877F2] hover:bg-[#166FE5] text-white transition-all hover:scale-105"
                data-testid="social-facebook"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                <span className="text-sm font-medium">Seguici su Facebook</span>
              </a>
              
              {/* Instagram - Coming Soon */}
              <div
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-slate-700/50 text-slate-400 cursor-not-allowed"
                title="Pagina Instagram in arrivo!"
                data-testid="social-instagram"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
                <span className="text-sm font-medium">Instagram</span>
                <span className="text-xs bg-slate-600 px-1.5 py-0.5 rounded">Presto!</span>
              </div>
            </div>
          </div>
          
          <div className="pt-8 border-t border-slate-800 flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Area Operatori - Link discreto in basso a sinistra */}
            <Link 
              to="/login?tipo=operatore" 
              className="flex items-center gap-2 text-slate-600 hover:text-cyan-400 transition-colors group"
              data-testid="footer-operatori-link"
            >
              <EnergyIcon size={24} className="opacity-50 group-hover:opacity-100 transition-opacity" />
              <span className="text-xs">Area Operatori</span>
            </Link>
            <p className="text-sm text-slate-500">
              © {new Date().getFullYear()} ENERGY MULTISERVIZI. Tutti i diritti riservati.
            </p>
          </div>
        </div>
      </footer>

      {/* Modal "IN ARRIVO" — Finanziamenti / Assicurazioni / Immobiliare */}
      <Dialog open={inArrivoOpen} onOpenChange={setInArrivoOpen}>
        <DialogContent
          className="bg-slate-950 border-2 border-amber-500/40 max-w-md text-white shadow-[0_0_40px_rgba(251,191,36,0.15)]"
          data-testid="modal-in-arrivo"
        >
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-orange-500/5 rounded-lg" />
          <DialogHeader className="relative">
            <div className="flex flex-col items-center gap-3 py-2">
              <div className="p-3 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 ring-4 ring-amber-500/20">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <DialogTitle
                className="text-2xl font-heading font-bold tracking-widest text-amber-300 text-center"
                data-testid="modal-in-arrivo-title"
              >
                IN ARRIVO
              </DialogTitle>
              {inArrivoService?.title && (
                <p className="text-sm uppercase tracking-wider text-slate-400">
                  {inArrivoService.title}
                </p>
              )}
            </div>
            <DialogDescription
              className="text-center text-slate-300 text-base leading-relaxed pt-2 relative"
              data-testid="modal-in-arrivo-description"
            >
              Stiamo siglando partnership dirette con i migliori operatori nazionali.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center pt-4">
            <Button
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white font-semibold px-8"
              onClick={() => setInArrivoOpen(false)}
              data-testid="modal-in-arrivo-close"
            >
              Ho capito
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Landing;
